// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module RealtimeBadge
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS realtime-visitor badge used by
 * dashboards to surface current activity at a glance. The badge handles
 * loading, empty, and active states without requiring consumer branching.
 */

import type { RealtimeStats } from "@geenius/analytics";
import { Badge } from "@geenius/ui/react-css";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import {
  type AnalyticsErrorStateValue,
  getAnalyticsErrorMessage,
} from "./ErrorState";

/**
 * Props for rendering the React CSS realtime activity badge.
 *
 * @property stats Latest realtime stats snapshot, or `null` when unavailable.
 * @property isLoading Whether the realtime fetch is still pending.
 * @property className Optional class name merged into the badge wrapper.
 */
export interface RealtimeBadgeProps {
  stats: RealtimeStats | null;
  isLoading: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders a React CSS badge showing the current active-visitor count.
 *
 * @param props Realtime stats and loading state for the badge.
 * @returns A badge element representing loading, empty, or active realtime state.
 */
export function RealtimeBadge(props: RealtimeBadgeProps): React.ReactElement {
  const copy = useAnalyticsCopy();

  if (props.error) {
    const retryLabel = copy.fallback("analytics.actions.retry", "Retry");

    return (
      <span
        className={cx(
          "gn-analytics-realtime",
          "gn-analytics-realtime--error",
          props.className,
        )}
        data-analytics-error-state
        role="alert"
        aria-live="assertive"
      >
        <span aria-hidden="true">!</span>
        <span>{getAnalyticsErrorMessage(props.error)}</span>
        {props.onRetry ? (
          <button
            type="button"
            className="gn-analytics-realtime__retry"
            onClick={props.onRetry}
          >
            {retryLabel}
          </button>
        ) : null}
      </span>
    );
  }

  if (props.isLoading) {
    const loadingLabel = copy.fallback(
      "analytics.realtime.loading",
      "Loading realtime analytics",
    );

    return (
      <Badge
        className={cx("gn-analytics-realtime", props.className)}
        variant="secondary"
        aria-live="polite"
        aria-label={loadingLabel}
      >
        <span aria-hidden="true">⏳</span>
        <span className="gn-analytics-a11y__sr-only">{loadingLabel}</span>
      </Badge>
    );
  }
  if (!props.stats) {
    const unavailableLabel = copy.fallback(
      "analytics.realtime.unavailable",
      "Realtime analytics unavailable",
    );

    return (
      <Badge
        className={cx("gn-analytics-realtime", props.className)}
        variant="secondary"
        aria-live="polite"
        aria-label={unavailableLabel}
      >
        <span aria-hidden="true">—</span>
        <span className="gn-analytics-a11y__sr-only">{unavailableLabel}</span>
      </Badge>
    );
  }

  const activeLabel = `${props.stats.activeVisitors} ${copy.t(
    "analytics.realtime.activeNow",
  )}`;

  return (
    <Badge
      className={cx("gn-analytics-realtime", props.className)}
      variant={props.stats.activeVisitors > 0 ? "success" : "secondary"}
      aria-live="polite"
      aria-label={activeLabel}
    >
      <span aria-hidden="true">🟢</span>
      <span className="gn-analytics-realtime__count">
        {props.stats.activeVisitors}
      </span>
      <span>{copy.t("analytics.realtime.activeNow")}</span>
    </Badge>
  );
}
